import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Alert,
  Card as AntCard,
  Col,
  Form,
  Grid,
  Input,
  Result,
  Row,
  Spin,
} from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  LinkOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { NO_PLUS_EMAIL_RULE } from 'utils/patterns';
import { Turnstile } from '@marsidev/react-turnstile';
import {
  Button,
  COLORS,
  Text,
  Title,
} from 'components';
import LadaNumero from 'components/LadaNumero';
import useQuery from 'hooks/useQuery';
import { patchActualizarContacto } from 'api/padrones';
import _logger from 'utils/logger';
import { siteKey } from 'utils/env';

const { useBreakpoint } = Grid;

const ActualizarContacto = () => {
  const screens = useBreakpoint();
  const [tokenFromUrl] = useQuery(['token']);
  const [form] = Form.useForm();
  const turnstileRef = useRef();
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState();
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const response = await patchActualizarContacto({
        token: tokenFromUrl,
        correo_electronico: values.correo_electronico,
        telefono_celular: values.telefono_celular,
        lada_celular: values.lada_celular,
        cf_turnstile_token: turnstileToken,
      });
      if (!response) return;
      setSuccessMessage(response.detail || 'Tu información de contacto fue actualizada correctamente.');
      setSuccess(true);
    } catch (err) {
      _logger(err);
    } finally {
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      setLoading(false);
    }
  };

  if (!tokenFromUrl) {
    return (
      <Row justify="center" style={{ margin: '40px 15px' }}>
        <Col xs={24} sm={20} md={16} lg={12}>
          <Alert
            type="error"
            showIcon
            icon={<LinkOutlined />}
            message="Enlace inválido"
            description="El enlace que intentas utilizar no es válido o ha expirado. Por favor, solicita un nuevo enlace desde el portal o contacta a soporte."
          />
        </Col>
      </Row>
    );
  }

  if (success) {
    return (
      <Row justify="center" style={{ margin: '40px 15px' }}>
        <Col xs={24} sm={20} md={16} lg={12}>
          <Wrapper>
            <Result
              status="success"
              icon={<CheckCircleOutlined style={{ color: COLORS.accent }} />}
              title="Información actualizada"
              subTitle={successMessage}
              extra={(
                <Link to="/mi-cuenta">
                  <Button $accent icon={<RightOutlined />}>
                    Ir a Mi Cuenta
                  </Button>
                </Link>
              )}
            />
          </Wrapper>
        </Col>
      </Row>
    );
  }

  return (
    <Spin spinning={loading}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <Wrapper
            title={(
              <Row justify="center">
                <Title level={2}>
                  Actualizar información de contacto
                </Title>
              </Row>
            )}
            style={{ margin: `15px 0 ${screens.md ? 100 : 140}px 15px` }}
          >
            <Row justify="center">
              <Col xs={24} sm={22} md={20} lg={18}>
                <Text style={{ position: 'relative', display: 'block', marginBottom: 20 }}>
                  Recibiste este enlace por correo electrónico. Por favor, confirma o actualiza
                  tus datos de contacto para poder acceder al portal y recibir notificaciones.
                </Text>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={turnstileToken ? onFinish : undefined}
                  initialValues={{ lada_celular: 34 }}
                  preserve={false}
                >
                  <Form.Item
                    rules={[{ required: true }, { type: 'email' }, NO_PLUS_EMAIL_RULE]}
                    label="Correo electrónico"
                    name="correo_electronico"
                  >
                    <Input disabled={loading} placeholder="Ingrese su correo electrónico" />
                  </Form.Item>
                  <LadaNumero
                    form={form}
                    names={{
                      lada: 'lada_celular',
                      numero_de_telefono: 'telefono_celular',
                    }}
                    label="Teléfono"
                    hasFeedback
                    fullSize
                  />
                  <Form.Item noStyle>
                    <Turnstile
                      siteKey={siteKey}
                      onSuccess={(cf_turnstile_token) => setTurnstileToken(cf_turnstile_token)}
                      onError={() => setTurnstileToken(null)}
                      onExpire={() => setTurnstileToken(null)}
                      ref={turnstileRef}
                    />
                  </Form.Item>
                  <Row justify="end" style={{ marginTop: 20 }}>
                    <Button
                      $accent
                      loading={loading}
                      disabled={!turnstileToken}
                      icon={<EditOutlined />}
                      onClick={form.submit}
                    >
                      Actualizar
                    </Button>
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </Col>
            </Row>
          </Wrapper>
        </Col>
      </Row>
    </Spin>
  );
};

const Wrapper = styled(AntCard)`
  border-radius: 0.5rem;
  box-shadow: 0 .5rem 1rem rgba(0, 0, 0, 0.15);
  width: auto;
  height: auto;

  .ant-card-head {
    line-height: 1.2;
    margin-bottom: 10px;
    overflow: hidden;
    height: auto;
    .ant-typography {
      white-space: break-spaces;
    }
  }
  .ant-card-body {
    padding: 1rem 1rem;
  }
`;

export default ActualizarContacto;
